import { isDefined, isNotDefined } from '@sgme/fp';
import { type ChangeEvent, useContext } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { FixingContext, useValidateFixing } from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectFixingErrors,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromFixingTile,
} from '@/store/orders/fixing/selectors.ts';

type FixingMarginTypeProps = {
  marginType: 'MarginInBps' | 'BidAskMidSpread' | undefined;
  setMarginType: (value: 'MarginInBps' | 'BidAskMidSpread' | undefined) => void;
  priceType: 'MID' | 'BIDASK' | undefined;
};

export const FixingEditingMarginType = (props: FixingMarginTypeProps) => {
  const { priceType, marginType, setMarginType } = props;
  const tileId = useContext(FixingContext).tileId;
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromFixingTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const fixingMarginType =
    marginType ?? (isDefined(orderFromBlotter?.fixingMarginType) ? orderFromBlotter?.fixingMarginType : undefined);
  const errors = useAppSelector((state) => selectFixingErrors(state, tileId));

  const marginTypeError = errors?.fixingMarginType;

  const validateFixing = useValidateFixing();

  const handleMarginTypeChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    track('Order action', 'Margin type changed');
    const newMarginType = event.target.value as 'MarginInBps' | 'BidAskMidSpread';
    if (isDefined(orderFromBlotter)) {
      const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
        ? orderFromBlotter.currencyPair.split('/')
        : ['', ''];

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error(`[handleMarginTypeChange] Invalid currency pair: ${currency1}/${currency2}`);
        return;
      }

      const response = await validateFixing({
        fixingMarginType: newMarginType,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setMarginType(newMarginType);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.marginType">
      <OmsFieldErrorWrapper errors={marginTypeError}>
        <Form.Select
          value={fixingMarginType}
          onChange={handleMarginTypeChange}
          size="sm"
          data-e2e="margin-type-selector"
        >
          <option key="MarginInBps" value="MarginInBps">
            <FormattedMessage id="order.form.marginType.MarginInBps" />
          </option>
          {((orderFromBlotter?.fixingPriceType === 'MID' && isNotDefined(priceType)) || priceType === 'MID') && (
            <option key="BidAskMidSpread" value="BidAskMidSpread">
              <FormattedMessage id="order.form.marginType.BidAskMidSpread" />
            </option>
          )}
        </Form.Select>
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
